import React, { useState } from 'react';
import { PatientInfoFormData } from './PatientInfoStep';
import { bookAppointment, bookBulkAppointments, rescheduleAppointment } from '../../utils/apis';
import styles from './ConfirmationStep.module.css';
import { AxisFailurePopup } from './AxisFailurePopup';
import { getUserFriendlyErrorMessage } from '../../utils/errorHandler';
import AxisLogo from './SVGs/AxisLogo';

interface ConfirmationStepProps {
  onBack: () => void;
  onSuccess: () => void;
  provider: Provider;
  slot: any;
  appointmentType: 'telehealth' | 'inperson' | 'spravato' | null;
  patientInfo: PatientInfoFormData | null;
  patientStatus?: 'existing' | 'new';
  patientData?: any;
  reschedulingAppointment?: any;
  selectedAppointments?: SelectedAppointment[];
}

interface Provider {
  providerId: string;
  displayName?: string;
  schedulingName?: string;
  firstName: string;
  lastName: string;
  specialty?: string;
  departmentId: string;
  departmentName: string;
  departmentZip?: string;
  departmentAddress?: string;
  departmentCity?: string;
  departmentState?: string;
  availableSlots: {
    startTime: string;
    endTime: string;
    duration?: number;
    appointmentId: string;
    appointmentName?: string;
    appointmentTypeId: string;
  }[];
}

interface SelectedAppointment {
  provider: Provider;
  slot: {
    startTime: string;
    endTime?: string;
    appointmentId: string;
    appointmentTypeId: string;
    [key: string]: any;
  };
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({ onBack, onSuccess, provider, slot, appointmentType, patientInfo, patientStatus, patientData, reschedulingAppointment, selectedAppointments = [] }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Extract patient data with proper fallback logic
  const getPatientData = () => {
    // For existing patients, use patientData from PatientStatusStep
    if (patientData?.athenaPatientId) {
      return patientData;
    }
    // For new patients, use patientInfo from PatientInfoStep
    return patientInfo || patientData;
  };

  const currentPatientData = getPatientData();

  const validSelectedAppointments = React.useMemo(
    () =>
      (selectedAppointments || []).filter(
        (item): item is SelectedAppointment =>
          Boolean(item?.provider && item?.slot && item.slot?.appointmentId)
      ),
    [selectedAppointments]
  );

  const primaryProvider = provider || validSelectedAppointments[0]?.provider || null;
  const primarySlot = slot || validSelectedAppointments[0]?.slot || null;

  const parseSlotDate = React.useCallback((value: string) => {
    try {
      if (value && value.includes('T')) {
        const [datePart, timePart] = value.split('T');
        const [month, day, year] = datePart.split('/');
        const normalizedMonth = month ? month.padStart(2, '0') : '';
        const normalizedDay = day ? day.padStart(2, '0') : '';
        if (year && normalizedMonth && normalizedDay) {
          const normalizedTime =
            timePart && timePart.length === 5 && !timePart.endsWith(':00')
              ? `${timePart}:00`
              : timePart || '00:00:00';
          return new Date(`${year}-${normalizedMonth}-${normalizedDay}T${normalizedTime}`);
        }
      }
      const fallback = new Date(value);
      if (Number.isNaN(fallback.getTime())) {
        return new Date();
      }
      return fallback;
    } catch (err) {
      return new Date();
    }
  }, []);

  const isBulkSpravato =
    !reschedulingAppointment &&
    appointmentType === 'spravato' &&
    !!patientData?.athenaPatientId &&
    validSelectedAppointments.length > 0;

  const appointmentsToDisplay = React.useMemo(() => {
    if (isBulkSpravato) {
      const clone = [...validSelectedAppointments];
      clone.sort(
        (a, b) => parseSlotDate(a.slot.startTime).getTime() - parseSlotDate(b.slot.startTime).getTime()
      );
      return clone;
    }
    if (primaryProvider && primarySlot) {
      return [{ provider: primaryProvider, slot: primarySlot }];
    }
    return [];
  }, [isBulkSpravato, validSelectedAppointments, parseSlotDate, primaryProvider, primarySlot]);

  const bookingButtonLabel = reschedulingAppointment
    ? 'Reschedule Appointment'
    : isBulkSpravato && appointmentsToDisplay.length > 1
    ? 'Book Appointments'
    : 'Book Appointment';

  const bookingButtonLoadingLabel = reschedulingAppointment ? 'Rescheduling...' : 'Booking...';

  const handleBook = async () => {
    if (!appointmentType || !patientStatus) return;
    if (!isBulkSpravato && (!primaryProvider || !primarySlot)) return;
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Format date of birth for API (normalize to MM/DD/YYYY)
      const formatDateOfBirth = (dob: any) => {
        const monthNameToNumber: Record<string, string> = {
          january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
          july: '07', august: '08', september: '09', october: '10', november: '11', december: '12'
        };
        if (typeof dob === 'string') {
          const trimmed = dob.trim();
          // If format like DD/Month/YYYY (e.g., 24/August/1970)
          const parts = trimmed.split('/');
          if (parts.length === 3 && isNaN(Number(parts[1]))) {
            const [ddRaw, monthName, yyyy] = parts;
            const dd = ddRaw.padStart(2, '0');
            const mm = (monthNameToNumber[monthName.toLowerCase()] || monthName).toString().padStart(2, '0');
            return `${mm}/${dd}/${yyyy}`;
          }
          // If ISO YYYY-MM-DD
          if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            const [yyyy, mm, dd] = trimmed.split('-');
            return `${mm}/${dd}/${yyyy}`;
          }
          // If already looks like MM/DD/YYYY just return
          return trimmed;
        }
        const { day, month, year } = dob || {};
        if (!day || !month || !year) {
          throw new Error('Date of birth is incomplete');
        }
        const mm = (monthNameToNumber[(month as string).toLowerCase()] || `${month}`).padStart(2, '0');
        const dd = `${day}`.padStart(2, '0');
        return `${mm}/${dd}/${year}`;
      };

      const computeDateTime = (slotValue: any) => {
      let appointmentDate = '';
      let appointmentTime = '';
        if (slotValue?.startTime) {
          if (slotValue.startTime.includes('T')) {
            const [datePart, timePart] = slotValue.startTime.split('T');
            appointmentDate = datePart;
            appointmentTime = timePart;
        } else {
            appointmentTime = slotValue.startTime;
          const today = new Date();
          const month = (today.getMonth() + 1).toString().padStart(2, '0');
          const day = today.getDate().toString().padStart(2, '0');
          const year = today.getFullYear();
          appointmentDate = `${month}/${day}/${year}`;
        }
      }
        return { appointmentDate, appointmentTime };
      };

      if (reschedulingAppointment) {
        if (!primaryProvider || !primarySlot) {
          throw new Error('Missing appointment details for rescheduling.');
        }
        await rescheduleAppointment({
          appointmentId: reschedulingAppointment.appointmentid || reschedulingAppointment.athenaAppointmentId || '',
          targetAppointmentId: primarySlot.appointmentId,
          departmentId: primaryProvider.departmentId,
          providerId: primaryProvider.providerId,
          patientId: patientData?.athenaPatientId,
          bookingNote: 'Patient requested reschedule'
        });
      } else if (isBulkSpravato) {
        if (!patientData?.athenaPatientId) {
          throw new Error('Unable to locate existing patient record for Spravato booking.');
        }
        const items = validSelectedAppointments
          .map((item) => {
            const { appointmentDate, appointmentTime } = computeDateTime(item.slot);
            return {
              appointmentId: item.slot.appointmentId,
              departmentId: item.provider.departmentId,
              providerId: item.provider.providerId,
              appointmentDate,
              appointmentTime
            };
          })
          .filter((item) => item.appointmentId && item.departmentId && item.providerId);

        if (!items.length) {
          throw new Error('No appointments selected. Please choose at least one time slot.');
        }

        const missingSchedule = items.find(entry => !entry.appointmentDate || !entry.appointmentTime);
        if (missingSchedule) {
          throw new Error('Unable to determine the appointment date or time for one of the selected slots.');
        }

        const appointmentTypeId =
          validSelectedAppointments[0].slot.appointmentTypeId ||
          primarySlot?.appointmentTypeId;

        if (!appointmentTypeId) {
          throw new Error('Unable to determine appointment type for bulk booking.');
        }

        const bulkPayload = {
          patientId: patientData.athenaPatientId,
          reasonType: patientStatus,
          appointmentTypeId,
          reason: 'Spravato treatment',
          insuranceCompany: patientInfo?.insurance && patientInfo.insurance.trim() !== '' ? patientInfo.insurance : undefined,
          insuranceIdNumber: patientInfo?.insuranceId && patientInfo.insuranceId.trim() !== '' ? patientInfo.insuranceId : undefined,
          referralSource: patientInfo?.howDidYouHear || undefined,
          reasonForVisit: patientInfo?.reasonForVisit || undefined,
          items
        };

        await bookBulkAppointments(bulkPayload);
      } else {
        if (!primaryProvider || !primarySlot) {
          throw new Error('Missing appointment details.');
        }

        const { appointmentDate, appointmentTime } = computeDateTime(primarySlot);

      const appointmentData: any = {
          appointmentId: primarySlot.appointmentId,
          departmentId: primaryProvider.departmentId,
          providerId: primaryProvider.providerId,
          appointmentTypeId: primarySlot.appointmentTypeId,
          reason:
            appointmentType === 'telehealth'
              ? 'Telehealth consultation'
              : appointmentType === 'spravato'
              ? 'Spravato treatment'
              : 'In-person consultation',
        reasonType: patientStatus,
        insuranceCompany: patientInfo?.insurance && patientInfo.insurance.trim() !== '' ? patientInfo.insurance : undefined,
        insuranceIdNumber: patientInfo?.insuranceId && patientInfo.insuranceId.trim() !== '' ? patientInfo.insuranceId : undefined,
          appointmentDate,
          appointmentTime
      };

      if (patientData?.athenaPatientId) {
        appointmentData.patientId = patientData.athenaPatientId;
      }

      if (patientStatus === 'new') {
        const patientDetails = currentPatientData;
        appointmentData.firstName = patientDetails.firstName;
        appointmentData.lastName = patientDetails.lastName;
        appointmentData.dateOfBirth = formatDateOfBirth(patientDetails.dateOfBirth);
        const formatSex = (sex: string) => {
          if (!sex) return 'U';
          const upperSex = sex.toUpperCase();
          if (upperSex === 'MALE' || upperSex === 'M') return 'M';
          if (upperSex === 'FEMALE' || upperSex === 'F') return 'F';
          if (upperSex === 'OTHER' || upperSex === 'O') return '';
            return 'U';
        };
        appointmentData.sex = formatSex(patientDetails.sex);
        appointmentData.phone = patientDetails.phoneNumber;
        const normalizePhoneType = (phoneType: string) => {
            if (!phoneType) return 'mobile';
          const normalized = phoneType.toLowerCase();
          return normalized === 'home' ? 'home' : 'mobile';
        };
        appointmentData.phoneType = normalizePhoneType(patientDetails.phoneType);
        appointmentData.email = patientDetails.email;
        appointmentData.address1 = patientDetails.address || 'Not provided';
          appointmentData.city = 'Aurora';
          appointmentData.state = 'CO';
        appointmentData.zip = patientDetails.zipCode || 'Not provided';
      }

        await bookAppointment(appointmentData);
      }
      
      setSuccess(true);
      // Redirect to thank you page after successful booking
      window.location.href = 'https://axismh.com/thank-you/';
      onSuccess();
    } catch (err: any) {
      const userFriendlyError = getUserFriendlyErrorMessage(
        err,
        isBulkSpravato ? 'booking appointments' : 'booking appointment'
      );
      setError(userFriendlyError);
    } finally {
      setLoading(false);
    }
  };


  const formatDateTime = (dateTime: string) => {
    try {
      // Convert the date format from "09/08/2025T08:00" to ISO format
      const parts = dateTime.split('T');
      if (parts.length === 2) {
        const datePart = parts[0]; // "09/08/2025"
        const timePart = parts[1]; // "08:00"
        const [month, day, year] = datePart.split('/');
        const isoDateString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart}:00`;
        const date = new Date(isoDateString);
        return date.toLocaleString([], { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true 
        });
      }
      const date = new Date(dateTime);
      return date.toLocaleString([], { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true 
      });
    } catch (error) {
      // Fallback to original string if parsing fails
      return dateTime;
    }
  };

  return (
    <div className={styles.confirmation}>
      {/* Header/Navigation */}
      <div className={styles.navBar}>
        <AxisLogo variant="white" size="extra-large" className={styles.navLogo} onClick={() => window.open('https://www.axismh.com/', '_blank')} style={{ cursor: 'pointer' }} />
        <span className={styles.navPhone}>(720) 400-7025</span>
        <div className={styles.navLinks}>
          <button className={styles.navLink} onClick={() => window.location.reload()}>HOME</button>
          {appointmentType === 'spravato' && (
            <a
              href="https://go.axismh.com/spravato-prep"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.navLink}
              title="Spravato Preparation Tips & Help"
            >
              Help
            </a>
          )}
        </div>
        {/* <div className={styles.navMenuIcon}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12h18M3 6h18M3 18h18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div> */}
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <h1 className={styles.title}>
          {patientData?.athenaPatientId ? 'Confirm Your Appointment' : 'Patient Information'}
        </h1>
        <p className={styles.subtitle}>
          {patientData?.athenaPatientId 
            ? 'Please review your appointment details and confirm your booking.'
            : 'Please provide your personal and insurance information to complete your booking.'
          }
        </p>

        <div className={`${styles.cardsContainer} ${isBulkSpravato ? styles.singleCard : ''}`}>
          {/* Appointment Details Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <svg className={styles.cardIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <div className={styles.cardTitle}>Appointment Details</div>
            </div>
            <div className={styles.cardBody}>
              {isBulkSpravato ? (
                <>
                  <div className={styles.providerName}>
                    {appointmentsToDisplay.length} Spravato appointment{appointmentsToDisplay.length > 1 ? 's' : ''} selected
                  </div>
                  <div className={styles.bulkDescription}>
                    Please review each visit before confirming your booking.
                  </div>
                  <div className={styles.appointmentList}>
                    {appointmentsToDisplay.map((item, index) => (
                      <div
                        key={`${item.slot.appointmentId}-${index}`}
                        className={styles.appointmentListItem}
                      >
                        <div className={styles.appointmentListHeader}>
                          <span>Appointment {index + 1}</span>
                          <span>{formatDateTime(item.slot.startTime)}</span>
                        </div>
                        <div className={styles.appointmentListMeta}>
                          {item.provider.firstName} {item.provider.lastName}
                        </div>
                        <div className={styles.appointmentListLocation}>
                          {[item.provider.departmentAddress || item.provider.departmentName, item.provider.departmentCity, item.provider.departmentState, item.provider.departmentZip]
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={styles.divider} />
                </>
              ) : (
                <>
                  <div className={styles.providerName}>
                    {primaryProvider?.firstName} {primaryProvider?.lastName}
                  </div>
                  <div className={styles.providerAddress}>
                    {[
                      primaryProvider?.departmentAddress || primaryProvider?.departmentName,
                      primaryProvider?.departmentCity,
                      primaryProvider?.departmentState,
                      primaryProvider?.departmentZip
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
              <div className={styles.providerPhone}>(720) 400-7025</div>
              
              <div className={styles.divider} />
              
              <div className={styles.tagsContainer}>
                <div className={styles.tagRow}>
                  <div className={styles.tagLabel}>Date & Time:</div>
                  <div className={styles.tag}>
                        {primarySlot?.startTime ? formatDateTime(primarySlot.startTime) : ''}
                  </div>
                </div>
                <div className={styles.tagRow}>
                  <div className={styles.tagLabel}>Type:</div>
                  <div className={styles.tag}>
                        {appointmentType === 'telehealth'
                          ? 'Telehealth'
                          : appointmentType === 'spravato'
                          ? 'Spravato'
                          : 'In-person'}
                  </div>
                </div>
              </div>
              
              <div className={styles.divider} />
                </>
              )}
            </div>
          </div>

          {/* Patient Information Card - Only show for regular (non-bulk Spravato) bookings */}
          {!isBulkSpravato && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <svg className={styles.cardIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/>
                  <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <div className={styles.cardTitle}>
                  {patientData?.athenaPatientId ? 'Your Information' : 'Patient Information'}
                </div>
              </div>
              <div className={styles.cardBody}>
                <div className={styles.patientName}>
                  {currentPatientData?.firstName} {currentPatientData?.lastName}
                </div>
                <div className={styles.patientContact}>
                  <div>{currentPatientData?.email}</div>
                  <div>{currentPatientData?.phoneNumber || currentPatientData?.phone}</div>
                </div>
                
                <div className={styles.divider} />
                
                <div className={styles.patientDetails}>
                  <div className={styles.detailRow}>
                    <div className={styles.detailLabel}>Date of Birth:</div>
                    <div className={styles.detailValue}>
                      {(() => {
                        const dob = currentPatientData?.dateOfBirth;
                        if (typeof dob === 'string') {
                          // Handle string format from PatientStatusStep (MM/DD/YYYY)
                          return dob;
                        } else if (dob && typeof dob === 'object') {
                          // Handle object format from PatientInfoStep
                          return `${dob.month} ${dob.day}, ${dob.year}`;
                        }
                        return '';
                      })()}
                    </div>
                  </div>
                  <div className={styles.detailRow}>
                    <div className={styles.detailLabel}>Sex:</div>
                    <div className={styles.detailValue}>
                      {currentPatientData?.sex || 'Not specified'}
                    </div>
                  </div>
                </div>
                
                {/* Show address and insurance info for new patients or if available */}
                {patientInfo && (patientInfo?.address || patientInfo?.zipCode || (patientInfo?.insurance && patientInfo?.insurance.trim() !== '')) && (
                  <>
                    <div className={styles.divider} />
                    
                    {(patientInfo?.address || patientInfo?.zipCode) && (
                      <>
                        <div className={styles.addressLabel}>Address:</div>
                        <div className={styles.addressText}>
                          <div>{patientInfo?.address || 'Not provided'}</div>
                          <div>{patientInfo?.zipCode || ''}</div>
                        </div>
                      </>
                    )}
                    
                    {/* Only show insurance section if insurance is provided (not empty/null) */}
                    {patientInfo?.insurance && patientInfo?.insurance.trim() !== '' && (
                      <>
                        <div className={styles.divider} />
                        
                        <div className={styles.patientDetails}>
                          <div className={styles.detailRow}>
                            <div className={styles.detailLabel}>Insurance:</div>
                            <div className={styles.detailValue}>{patientInfo?.insurance}</div>
                          </div>
                          {patientInfo?.insuranceId && patientInfo?.insuranceId.trim() !== '' && (
                            <div className={styles.detailRow}>
                              <div className={styles.detailLabel}>Member ID:</div>
                              <div className={styles.detailValue}>{patientInfo?.insuranceId}</div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}
                
                {/* For existing patients without detailed info, show a note */}
                {patientData?.athenaPatientId && !patientInfo && (
                  <>
                    <div className={styles.divider} />
                    <div className={styles.existingPatientNote}>
                      ✓ Your information is on file with our system
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <button
          className={styles.bookButton}
          onClick={handleBook}
          disabled={loading || success}
        >
          {loading ? bookingButtonLoadingLabel : bookingButtonLabel}
        </button>

        {/* Friendly error modal */}
        <AxisFailurePopup
          isOpen={!!error}
          message={error || ''}
          onClose={() => setError(null)}
          title="We couldn't complete your booking"
        />

        {success && <div className={`${styles.message} ${styles.successMessage}`}>Appointment booked successfully!</div>}

        <button className={styles.backButton} onClick={onBack} disabled={loading}>
          ← Back
        </button>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerTitle}>Axis Integrated Mental Health</div>
        <div className={styles.footerInfo}>
          <div className={styles.footerInfoItem}>
            <span>📞</span>
            <span>Phone (appointments): 720-400-7025</span>
          </div>
          <div className={styles.footerInfoItem}>
            <span>📍</span>
          </div>
        </div>
        <div className={styles.rating}>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
        </div>
        <div className={styles.ratingText}>Average Rating: 4.9/5</div>
      </div>

      {/* Accessibility Icon */}
      <div className={styles.accessibilityIcon}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="4" r="2" fill="white"/>
          <path d="M12 6v8M8 14h8M12 18v4M8 22h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  );
};

export default ConfirmationStep; 